/**
 * AuthProvider — bootstraps the admin session.
 *
 * On mount it calls GET /api/auth/me:
 *  - 401  -> not signed in: redirect the browser to the OIDC login (BFF).
 *  - 200 but not a platform admin -> render a clear "access denied"
 *    message (the API also enforces this; we mirror it for UX).
 *  - 200 + admin -> expose the AuthMe to the app.
 *
 * Gate rule (mirrors requirePlatformAdmin): isPlatformAdmin === true. The API
 * additionally honors an optional ADMIN_EMAIL_DOMAINS allowlist.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { AuthMe } from '@opsrev/shared';
import { ApiError, fetchMe, redirectToLogin } from './api';

interface AuthState {
  me: AuthMe;
}

const AuthContext = createContext<AuthState | null>(null);

type Phase =
  | { kind: 'loading' }
  | { kind: 'authed'; me: AuthMe }
  | { kind: 'denied'; email: string }
  | { kind: 'error'; message: string };

function isPlatformAdmin(me: AuthMe): boolean {
  // The API is the source of truth (incl. any ADMIN_EMAIL_DOMAINS allowlist);
  // the client mirrors the is_platform_admin check for UX.
  return me.isPlatformAdmin;
}

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        if (isPlatformAdmin(me)) {
          setPhase({ kind: 'authed', me });
        } else {
          setPhase({ kind: 'denied', email: me.email });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          redirectToLogin();
          return; // navigation underway; keep showing the loader
        }
        const message =
          err instanceof Error ? err.message : 'Failed to load your session.';
        setPhase({ kind: 'error', message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (phase.kind === 'loading') {
    return (
      <div className="centered-screen">
        <img className="auth-logo" src="/opsrev-logo.png" alt="OpsRev" />
        <div className="spinner" aria-label="Loading" />
        <p className="muted">Signing you in…</p>
      </div>
    );
  }

  if (phase.kind === 'denied') {
    return (
      <div className="centered-screen">
        <img className="auth-logo" src="/opsrev-logo.png" alt="OpsRev" />
        <div className="card narrow">
          <h1>Access denied</h1>
          <p>
            You are signed in as <strong>{phase.email}</strong>, but this console
            is restricted to OpsRev platform administrators.
          </p>
          <p className="muted">
            If you believe this is a mistake, ask a platform admin to grant you
            access, then sign in again.
          </p>
          <a className="btn" href="/api/auth/logout">
            Sign out
          </a>
        </div>
      </div>
    );
  }

  if (phase.kind === 'error') {
    return (
      <div className="centered-screen">
        <img className="auth-logo" src="/opsrev-logo.png" alt="OpsRev" />
        <div className="card narrow">
          <h1>Something went wrong</h1>
          <p className="error-text">{phase.message}</p>
          <button className="btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ me: phase.me }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
